import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { useUrlParams, useUrlState } from '../hooks/useUrlState';
import { useAuth } from '../hooks/useAuth';
import { api, AllTimeIndividualRow, AllTimeTeamRow, CompanyReportRow, Role, User } from '../lib/api';
import { fmtCurrency, fmtMonth } from '../lib/fmt';
import {
  SectionLabel, Card, CardHeader, Table, Tr, Td, Badge,
  MetricCard, FilterBar, Select, ActionButton, Spinner, EmptyState, SortableTh,
} from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';

async function viewAsUser(
  user: Pick<User, 'jira_user_id' | 'display_name' | 'email' | 'role' | 'title' | 'team'>,
  startImpersonation: (u: User) => Promise<void>,
  navigate: (to: string) => void,
) {
  try {
    // Build a User-shaped object from the report row. Fields missing from
    // the report (is_admin, is_active, manager_jira_user_id, bonus_role)
    // don't affect impersonation — the backend looks the user up by id.
    await startImpersonation({
      ...user,
      bonus_role: null,
      manager_jira_user_id: null,
      is_admin: false,
      is_active: true,
    } as User);
    navigate('/scorecard');
  } catch (e) {
    alert(`Could not view as user: ${(e as Error).message}`);
  }
}

const ROLE_FILTERS: Array<{ value: string; label: string }> = [
  { value: '',   label: 'All bonus roles' },
  { value: 'IC', label: 'IC' },
  { value: 'DM', label: 'DM' },
  { value: 'SA', label: 'SA' },
];

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function pastMonthsOptions(count: number): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < count; i++) {
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
    d.setUTCMonth(d.getUTCMonth() - 1);
  }
  return out;
}

function csvEscape(v: string | number | null | undefined): string {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, rows: string[][]) {
  const body = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminReportsPage() {
  const [mode, setMode] = useUrlState('mode', 'period');
  if (mode === 'alltime') return <AllTimeView onSwitch={() => setMode('period')} />;
  return <PeriodView onSwitch={() => setMode('alltime')} />;
}

function PeriodView({ onSwitch }: { onSwitch: () => void }) {
  const defaults = useMemo(() => ({ month: currentMonthKey(), role: '', team: '' }), []);
  const [params, patchParams] = useUrlParams(defaults);
  // Apply/Clear pattern: draft accumulates UI edits; useQuery only fires
  // when the user clicks Apply (or presses Enter on a keyed input).
  const [draft, setDraft] = useState(params);
  useEffect(() => { setDraft(params); }, [params.month, params.role, params.team]);  // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = draft.month !== params.month || draft.role !== params.role || draft.team !== params.team;
  const apply = () => patchParams(draft);
  const clear = () => { setDraft(defaults); patchParams(defaults); };

  const { startImpersonation } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, isFetching, error } = useQuery(
    () => api.admin.companyReport(params.month),
    [params.month],
  );

  const allRows = data?.rows ?? [];

  // Client-side filters on top of the API response so changing role/team
  // doesn't need a round trip.
  const teams = useMemo(() => {
    const set = new Set<string>();
    for (const r of allRows) if (r.user.team) set.add(r.user.team);
    return [...set].sort();
  }, [allRows]);

  const filteredRows: CompanyReportRow[] = useMemo(() => {
    return allRows.filter((r) => {
      if (params.role && r.effective_bonus_role !== params.role) return false;
      if (params.team && r.user.team !== params.team) return false;
      return true;
    });
  }, [allRows, params.role, params.team]);

  const { sorted: sortedCompanyRows, sort: companySort } = useTableSort(
    filteredRows,
    {
      employee:   (r) => r.user.display_name,
      role:       (r) => r.effective_bonus_role,
      team:       (r) => r.user.team ?? '',
      shipped:    (r) => r.shipped_value,
      tier:       (r) => r.bonus_tier ?? -1,
      bonus:      (r) => r.gross_bonus ?? -1,
      wip:        (r) => r.total_wip,
      p_total:    (r) => r.potential_total,
      p_tier:     (r) => r.potential_tier ?? -1,
      p_bonus:    (r) => r.potential_gross_bonus ?? -1,
      p_delta:    (r) => r.potential_bonus_delta ?? -Infinity,
    },
    { key: 'employee', dir: 'asc' },
  );

  const totals = useMemo(
    () =>
      filteredRows.reduce(
        (acc, r) => {
          acc.shipped += r.shipped_value;
          acc.wip += r.total_wip;
          acc.actualBonus += r.gross_bonus ?? 0;
          acc.potentialBonus += r.potential_gross_bonus ?? 0;
          return acc;
        },
        { shipped: 0, wip: 0, actualBonus: 0, potentialBonus: 0 },
      ),
    [filteredRows],
  );

  function exportPayrollCsv() {
    if (!data) return;
    // Stripped-down format intended for direct ingest into a payroll system.
    // No tier / multiplier / potential fields — just who gets what.
    const header = ['Employee name', 'Email', 'Attribution period', 'Gross bonus (USD)'];
    const out = [header];
    for (const r of filteredRows) {
      if (!r.gross_bonus || r.gross_bonus <= 0) continue; // skip zero-bonus rows
      out.push([
        r.user.display_name,
        r.user.email,
        params.month,
        r.gross_bonus.toFixed(2),
      ]);
    }
    downloadCsv(`payroll-${params.month}.csv`, out);
  }

  function exportCsv() {
    if (!data) return;
    const header = [
      'User', 'Email', 'Org role', 'Bonus role', 'Title', 'Team',
      'Shipped value', 'Tier', 'Multiplier', 'Gross bonus', 'Locked',
      'Cumulative WIP (reserve)',
      'Potential total (shipped + WIP)', 'Potential tier', 'Potential multiplier', 'Potential gross bonus', 'Potential bonus delta',
    ];
    const out = [header];
    for (const r of filteredRows) {
      out.push([
        r.user.display_name,
        r.user.email,
        r.user.role,
        r.user.bonus_role ?? '',
        r.user.title,
        r.user.team,
        r.shipped_value.toFixed(2),
        r.bonus_tier ?? '',
        r.bonus_multiplier != null ? r.bonus_multiplier.toString() : '',
        r.gross_bonus != null ? r.gross_bonus.toFixed(2) : '',
        r.is_locked ? 'yes' : 'no',
        r.total_wip.toFixed(2),
        r.potential_total.toFixed(2),
        r.potential_tier ?? '',
        r.potential_multiplier != null ? r.potential_multiplier.toString() : '',
        r.potential_gross_bonus != null ? r.potential_gross_bonus.toFixed(2) : '',
        r.potential_bonus_delta != null ? r.potential_bonus_delta.toFixed(2) : '',
      ].map(String));
    }
    downloadCsv(`company-report-${params.month}.csv`, out);
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Company report — {fmtMonth(params.month)}</SectionLabel>
        <span style={{ marginLeft: 'auto' }}>
          <ModeToggle active="period" onSwitch={onSwitch} />
        </span>
      </div>

      <Card style={{ marginBottom: 10 }}>
        <FilterBar isFetching={isFetching} dirty={dirty} onApply={apply} onClear={clear}>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Period</span>
          <Select value={draft.month} onChange={(v) => setDraft({ ...draft, month: v })}>
            {pastMonthsOptions(24).map((m) => (
              <option key={m} value={m}>{fmtMonth(m)}</option>
            ))}
          </Select>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 12 }}>Role</span>
          <Select value={draft.role} onChange={(v) => setDraft({ ...draft, role: v as Role | '' })}>
            {ROLE_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 12 }}>Team</span>
          <Select value={draft.team} onChange={(v) => setDraft({ ...draft, team: v })}>
            <option value="">All teams</option>
            {teams.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
          <div style={{ display: 'flex', gap: 6 }}>
            <ActionButton
              onClick={exportPayrollCsv}
              variant="default"
              disabled={!data || filteredRows.every((r) => !r.gross_bonus)}
            >
              Payroll CSV
            </ActionButton>
            <ActionButton onClick={exportCsv} variant="default" disabled={!data || filteredRows.length === 0}>
              Full CSV
            </ActionButton>
          </div>
        </FilterBar>
      </Card>

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <EmptyState message={error} />
      ) : !data ? null : (
        <div style={{ opacity: isFetching ? 0.4 : 1, pointerEvents: isFetching ? 'none' : undefined, transition: 'opacity 120ms' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
            <MetricCard
              label={`Shipped value — ${fmtMonth(params.month)}`}
              value={fmtCurrency(totals.shipped)}
              accent="green"
              sub={`${filteredRows.length} bonus-eligible ${filteredRows.length === 1 ? 'employee' : 'employees'}`}
            />
            <MetricCard
              label="Gross bonus earned"
              value={fmtCurrency(totals.actualBonus)}
              accent="blue"
              sub="From shipped deliverables this period"
            />
            <MetricCard
              label="Cumulative WIP (reserve)"
              value={fmtCurrency(totals.wip)}
              accent="amber"
              sub="All-time unshipped — convertible to bonus"
            />
            <MetricCard
              label="Potential gross bonus"
              value={fmtCurrency(totals.potentialBonus)}
              accent="green"
              sub="If all WIP were shipped in this period"
            />
          </div>

          <Card>
            <CardHeader>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Per-employee breakdown</span>
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
                {filteredRows.length} of {allRows.length} employees shown
              </span>
            </CardHeader>
            {filteredRows.length === 0 ? (
              <EmptyState message="No employees match the current filters." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <SortableTh columnKey="employee" sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort}>Employee</SortableTh>
                    <SortableTh columnKey="role"     sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort}>Role</SortableTh>
                    <SortableTh columnKey="team"     sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort}>Team</SortableTh>
                    <SortableTh columnKey="shipped"  sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort} right>Shipped</SortableTh>
                    <SortableTh columnKey="tier"     sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort}>Tier</SortableTh>
                    <SortableTh columnKey="bonus"    sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort} right>Bonus</SortableTh>
                    <SortableTh columnKey="wip"      sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort} right>WIP reserve</SortableTh>
                    <SortableTh columnKey="p_total"  sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort} right>Potential total</SortableTh>
                    <SortableTh columnKey="p_tier"   sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort}>Potential tier</SortableTh>
                    <SortableTh columnKey="p_bonus"  sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort} right>Potential bonus</SortableTh>
                    <SortableTh columnKey="p_delta"  sortKey={companySort.sortKey} dir={companySort.dir} setSort={companySort.setSort} right>Δ vs earned</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {sortedCompanyRows.map((r) => (
                    <Tr key={r.user.jira_user_id}>
                      <Td>
                        <button
                          onClick={() => viewAsUser(r.user, startImpersonation, navigate)}
                          style={{
                            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            color: 'var(--color-blue)', textDecoration: 'underline',
                            fontWeight: 500, fontSize: 'inherit', textAlign: 'left',
                          }}
                          title="View this user's scorecard (starts an impersonation session)"
                        >
                          {r.user.display_name}
                        </button>
                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                          {r.user.title || '—'}
                          {r.is_locked && <span style={{ marginLeft: 6 }}><Badge variant="locked">Locked</Badge></span>}
                        </div>
                      </Td>
                      <Td>
                        <Badge variant="gray">{r.effective_bonus_role}</Badge>
                        {r.user.role !== r.effective_bonus_role && (
                          <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2 }}>
                            org: {r.user.role}
                          </div>
                        )}
                      </Td>
                      <Td muted style={{ fontSize: 11 }}>{r.user.team || '—'}</Td>
                      <Td right bold style={{ color: r.shipped_value > 0 ? 'var(--color-green)' : undefined }}>
                        {fmtCurrency(r.shipped_value)}
                      </Td>
                      <Td>
                        {r.bonus_tier
                          ? <Badge variant="green">Tier {r.bonus_tier}</Badge>
                          : <Badge variant="gray">—</Badge>}
                      </Td>
                      <Td right bold style={{ color: r.gross_bonus ? 'var(--color-green)' : undefined }}>
                        {r.gross_bonus != null ? fmtCurrency(r.gross_bonus) : '—'}
                      </Td>
                      <Td right style={{ color: r.total_wip > 0 ? 'var(--color-amber)' : undefined }}>
                        {fmtCurrency(r.total_wip)}
                      </Td>
                      <Td right muted>{fmtCurrency(r.potential_total)}</Td>
                      <Td>
                        {r.potential_tier
                          ? <Badge variant="blue">Tier {r.potential_tier}</Badge>
                          : <Badge variant="gray">—</Badge>}
                      </Td>
                      <Td right>
                        {r.potential_gross_bonus != null ? fmtCurrency(r.potential_gross_bonus) : '—'}
                      </Td>
                      <Td right muted>
                        {r.potential_bonus_delta != null && r.potential_bonus_delta > 0
                          ? <span style={{ color: 'var(--color-green-text)' }}>+{fmtCurrency(r.potential_bonus_delta)}</span>
                          : r.potential_bonus_delta != null
                          ? fmtCurrency(r.potential_bonus_delta)
                          : '—'}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
            "Potential" columns project what would happen if every hour of each employee's WIP reserve shipped within this period — tier is resolved against their effective comp configuration for this month.
          </div>
        </div>
      )}
    </>
  );
}

// ─── Mode toggle ────────────────────────────────────────────────────────────

function ModeToggle({ active, onSwitch }: { active: 'period' | 'alltime'; onSwitch: () => void }) {
  const otherLabel = active === 'period' ? 'All-time' : 'Period';
  return (
    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 11 }}>
      <span style={{ color: 'var(--color-text-muted)' }}>Report mode</span>
      <div style={{ display: 'inline-flex', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
        <button
          onClick={active === 'period' ? undefined : onSwitch}
          style={{
            fontSize: 11, padding: '4px 10px', cursor: active === 'period' ? 'default' : 'pointer', border: 'none',
            background: active === 'period' ? 'var(--color-blue)' : 'var(--color-surface)',
            color: active === 'period' ? '#fff' : 'var(--color-text)',
          }}
        >
          Period
        </button>
        <button
          onClick={active === 'alltime' ? undefined : onSwitch}
          style={{
            fontSize: 11, padding: '4px 10px', cursor: active === 'alltime' ? 'default' : 'pointer', border: 'none',
            background: active === 'alltime' ? 'var(--color-blue)' : 'var(--color-surface)',
            color: active === 'alltime' ? '#fff' : 'var(--color-text)',
          }}
          title={`Switch to ${otherLabel} view`}
        >
          All-time
        </button>
      </div>
    </div>
  );
}

// ─── All-time view ──────────────────────────────────────────────────────────

function AllTimeView({ onSwitch }: { onSwitch: () => void }) {
  const defaults = useMemo(() => ({ role: '', team: '', group: 'individual' }), []);
  const [params, patchParams] = useUrlParams(defaults);
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, isFetching, error } = useQuery(() => api.admin.allTimeReport());

  const individuals = data?.individuals ?? [];
  const teams = data?.teams ?? [];

  const teamsFromIndividuals = useMemo(() => {
    const set = new Set<string>();
    for (const r of individuals) if (r.user.team) set.add(r.user.team);
    return [...set].sort();
  }, [individuals]);

  const filteredIndividuals: AllTimeIndividualRow[] = useMemo(() => {
    return individuals.filter((r) => {
      if (params.role && r.effective_bonus_role !== params.role) return false;
      if (params.team && r.user.team !== params.team) return false;
      return true;
    });
  }, [individuals, params.role, params.team]);

  const { sorted: sortedIndividuals, sort: indivSort } = useTableSort(
    filteredIndividuals,
    {
      employee: (r) => r.user.display_name,
      role:     (r) => r.effective_bonus_role,
      team:     (r) => r.user.team ?? '',
      shipped:  (r) => r.shipped_value,
      wip:      (r) => r.wip_value,
      canceled: (r) => r.canceled_value,
      total:    (r) => r.total_value,
    },
    { key: 'shipped', dir: 'desc' },
  );
  const { sorted: sortedTeams, sort: teamSort } = useTableSort(
    teams,
    {
      leader:  (r) => r.director.display_name,
      role:    (r) => r.director.role,
      members: (r) => r.member_count,
      shipped: (r) => r.shipped_value,
      wip:     (r) => r.wip_value,
      canceled:(r) => r.canceled_value,
      total:   (r) => r.total_value,
    },
    { key: 'shipped', dir: 'desc' },
  );

  const filteredIndivTotals = useMemo(
    () =>
      filteredIndividuals.reduce(
        (acc, r) => {
          acc.shipped += r.shipped_value;
          acc.wip += r.wip_value;
          return acc;
        },
        { shipped: 0, wip: 0 },
      ),
    [filteredIndividuals],
  );

  function exportIndividualsCsv() {
    const header = ['User', 'Org role', 'Bonus role', 'Title', 'Team', 'Shipped (all-time)', 'WIP (not shipped)', 'Total'];
    const rows = [header];
    for (const r of filteredIndividuals) {
      rows.push([
        r.user.display_name, r.user.role, r.user.bonus_role ?? '', r.user.title, r.user.team,
        r.shipped_value.toFixed(2), r.wip_value.toFixed(2), r.total_value.toFixed(2),
      ]);
    }
    downloadCsv('alltime-individuals.csv', rows);
  }

  function exportTeamsCsv() {
    const header = ['Director', 'Role', 'Title', 'Team', 'Member count', 'Shipped (all-time)', 'WIP (not shipped)', 'Total', 'Includes director work'];
    const rows = [header];
    for (const r of teams) {
      rows.push([
        r.director.display_name, r.director.role, r.director.title, r.director.team,
        String(r.member_count), r.shipped_value.toFixed(2), r.wip_value.toFixed(2), r.total_value.toFixed(2),
        r.includes_personal ? 'yes' : 'no',
      ]);
    }
    downloadCsv('alltime-teams.csv', rows);
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>All-time report</SectionLabel>
        <span style={{ marginLeft: 'auto' }}>
          <ModeToggle active="alltime" onSwitch={onSwitch} />
        </span>
      </div>

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <EmptyState message={error} />
      ) : !data ? null : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
            <MetricCard
              label="Shipped value (all time)"
              value={fmtCurrency(data.totals.shipped_value)}
              accent="green"
              sub={`${individuals.length} contributors`}
            />
            <MetricCard
              label="WIP (not yet shipped)"
              value={fmtCurrency(data.totals.wip_value)}
              accent="amber"
              sub="Everything in flight, no date filter"
            />
            <MetricCard
              label="Combined"
              value={fmtCurrency(data.totals.shipped_value + data.totals.wip_value)}
              accent="blue"
              sub="Shipped + WIP"
            />
            <MetricCard
              label="Canceled (forfeit)"
              value={fmtCurrency(data.totals.canceled_value)}
              accent={data.totals.canceled_value > 0 ? 'amber' : undefined}
              sub="Never shipped"
            />
          </div>

          {/* Group selector: individual | team */}
          <Card style={{ marginBottom: 0 }}>
            <FilterBar
              isFetching={isFetching}
              onClear={() => patchParams(defaults)}
            >
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>View</span>
              <Select value={params.group} onChange={(v) => patchParams({ group: v })}>
                <option value="individual">By individual</option>
                <option value="team">By team / director</option>
              </Select>
              {params.group === 'individual' && (
                <>
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 12 }}>Role</span>
                  <Select value={params.role} onChange={(v) => patchParams({ role: v as Role | '' })}>
                    <option value="">All bonus roles</option>
                    <option value="IC">IC</option>
                    <option value="DM">DM</option>
                    <option value="SA">SA</option>
                  </Select>
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 12 }}>Team</span>
                  <Select value={params.team} onChange={(v) => patchParams({ team: v })}>
                    <option value="">All teams</option>
                    {teamsFromIndividuals.map((t) => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </>
              )}
              <div>
                <ActionButton
                  onClick={params.group === 'individual' ? exportIndividualsCsv : exportTeamsCsv}
                  variant="default"
                  disabled={(params.group === 'individual' ? filteredIndividuals : teams).length === 0}
                >
                  Export CSV
                </ActionButton>
              </div>
            </FilterBar>
          </Card>

          {params.group === 'individual' ? (
            <Card>
              <CardHeader>
                <span style={{ fontSize: 12, fontWeight: 500 }}>By individual — sorted by total</span>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
                  {filteredIndividuals.length} of {individuals.length} shown · filter total:{' '}
                  shipped {fmtCurrency(filteredIndivTotals.shipped)}, WIP {fmtCurrency(filteredIndivTotals.wip)}
                </span>
              </CardHeader>
              {filteredIndividuals.length === 0 ? (
                <EmptyState message="No individuals match the current filters." />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <SortableTh columnKey="employee" sortKey={indivSort.sortKey} dir={indivSort.dir} setSort={indivSort.setSort}>Employee</SortableTh>
                      <SortableTh columnKey="role"     sortKey={indivSort.sortKey} dir={indivSort.dir} setSort={indivSort.setSort}>Bonus role</SortableTh>
                      <SortableTh columnKey="team"     sortKey={indivSort.sortKey} dir={indivSort.dir} setSort={indivSort.setSort}>Team</SortableTh>
                      <SortableTh columnKey="shipped"  sortKey={indivSort.sortKey} dir={indivSort.dir} setSort={indivSort.setSort} right>Shipped (all-time)</SortableTh>
                      <SortableTh columnKey="wip"      sortKey={indivSort.sortKey} dir={indivSort.dir} setSort={indivSort.setSort} right>WIP</SortableTh>
                      <SortableTh columnKey="canceled" sortKey={indivSort.sortKey} dir={indivSort.dir} setSort={indivSort.setSort} right>Canceled (forfeit)</SortableTh>
                      <SortableTh columnKey="total"    sortKey={indivSort.sortKey} dir={indivSort.dir} setSort={indivSort.setSort} right>Total</SortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedIndividuals.map((r) => (
                      <Tr key={r.user.jira_user_id}>
                        <Td>
                          <button
                            onClick={() => viewAsUser({ ...r.user, email: '' }, startImpersonation, navigate)}
                            style={{
                              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                              color: 'var(--color-blue)', textDecoration: 'underline',
                              fontWeight: 500, fontSize: 'inherit', textAlign: 'left',
                            }}
                            title="View this user's scorecard"
                          >
                            {r.user.display_name}
                          </button>
                          <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{r.user.title || '—'}</div>
                        </Td>
                        <Td>
                          <Badge variant="gray">{r.effective_bonus_role}</Badge>
                          {r.user.role !== r.effective_bonus_role && (
                            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2 }}>
                              org: {r.user.role}
                            </div>
                          )}
                        </Td>
                        <Td muted style={{ fontSize: 11 }}>{r.user.team || '—'}</Td>
                        <Td right bold style={{ color: 'var(--color-green)' }}>{fmtCurrency(r.shipped_value)}</Td>
                        <Td right style={{ color: 'var(--color-amber)' }}>{fmtCurrency(r.wip_value)}</Td>
                        <Td right muted style={{ color: r.canceled_value > 0 ? 'var(--color-red-text)' : undefined }}>
                          {r.canceled_value > 0 ? fmtCurrency(r.canceled_value) : '—'}
                        </Td>
                        <Td right bold>{fmtCurrency(r.total_value)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <span style={{ fontSize: 12, fontWeight: 500 }}>By team / director — sorted by total</span>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
                  {teams.length} leaders · subtree totals include direct + indirect reports
                </span>
              </CardHeader>
              {teams.length === 0 ? (
                <EmptyState message="No directors or execs with active reports." />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <SortableTh columnKey="leader"  sortKey={teamSort.sortKey} dir={teamSort.dir} setSort={teamSort.setSort}>Director / Exec</SortableTh>
                      <SortableTh columnKey="role"    sortKey={teamSort.sortKey} dir={teamSort.dir} setSort={teamSort.setSort}>Role</SortableTh>
                      <SortableTh columnKey="members" sortKey={teamSort.sortKey} dir={teamSort.dir} setSort={teamSort.setSort} right>Members in tree</SortableTh>
                      <SortableTh columnKey="shipped" sortKey={teamSort.sortKey} dir={teamSort.dir} setSort={teamSort.setSort} right>Shipped (all-time)</SortableTh>
                      <SortableTh columnKey="wip"     sortKey={teamSort.sortKey} dir={teamSort.dir} setSort={teamSort.setSort} right>WIP</SortableTh>
                      <SortableTh columnKey="canceled" sortKey={teamSort.sortKey} dir={teamSort.dir} setSort={teamSort.setSort} right>Canceled (forfeit)</SortableTh>
                      <SortableTh columnKey="total"   sortKey={teamSort.sortKey} dir={teamSort.dir} setSort={teamSort.setSort} right>Total</SortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTeams.map((r: AllTimeTeamRow) => (
                      <Tr key={r.director.jira_user_id}>
                        <Td>
                          <div style={{ fontWeight: 500 }}>{r.director.display_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                            {r.director.title || '—'}
                            {r.includes_personal && (
                              <span style={{ marginLeft: 6 }}>
                                <Badge variant="blue">+ player/coach</Badge>
                              </span>
                            )}
                          </div>
                        </Td>
                        <Td><Badge variant="gray">{r.director.role}</Badge></Td>
                        <Td right muted>{r.member_count}</Td>
                        <Td right bold style={{ color: 'var(--color-green)' }}>{fmtCurrency(r.shipped_value)}</Td>
                        <Td right style={{ color: 'var(--color-amber)' }}>{fmtCurrency(r.wip_value)}</Td>
                        <Td right muted style={{ color: r.canceled_value > 0 ? 'var(--color-red-text)' : undefined }}>
                          {r.canceled_value > 0 ? fmtCurrency(r.canceled_value) : '—'}
                        </Td>
                        <Td right bold>{fmtCurrency(r.total_value)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          )}

          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
            Shipped = billable hours × billable rate on every Completed deliverable the user contributed to, ever. WIP = same but for deliverables not yet Completed. Rates are the current rates on each deliverable — correcting a rate flows through to all unlocked historical totals on the next refresh.
          </div>
        </>
      )}
    </>
  );
}
